
import { useState, useCallback } from 'react';
import { pdf } from '@react-pdf/renderer';
import { FileDown, Loader2 } from 'lucide-react';
import Button from '../ui/Button';
import { ArticlePDF } from './ArticlePDF';
import type { Article } from '../../types';
import { generatePDFFilename } from '../../utils/pdf';
import { trackDownload } from '../../lib/analytics';

export interface PDFDownloadButtonProps {
  /** Article data to generate PDF from */
  article: Article;
  /** Custom button text (default: "Als PDF herunterladen") */
  buttonText?: string;
  /** Custom filename (optional - auto-generated from title if not provided) */
  filename?: string;
  /** Site name for PDF header */
  siteName?: string;
  /** Site URL for PDF footer */
  siteUrl?: string;
  /** Button variant */
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  /** Button size */
  size?: 'sm' | 'md' | 'lg';
  /** Additional CSS classes */
  className?: string;
  /** Callback when PDF generation starts */
  onGenerateStart?: () => void;
  /** Callback when PDF generation completes successfully */
  onGenerateComplete?: (filename: string) => void;
  /** Callback when PDF generation fails */
  onGenerateError?: (error: Error) => void;
}

/**
 * PDFDownloadButton Component
 * 
 * A button that generates and downloads a PDF document from article data.
 * Shows a loading state while the PDF is being generated.
 * 
 * @example
 * ```tsx
 * <PDFDownloadButton 
 *   article={article}
 *   variant="outline"
 *   size="sm"
 * />
 * ```
 */
export function PDFDownloadButton({
  article,
  buttonText = 'Als PDF herunterladen',
  filename,
  siteName = 'Sternwarte Mannheim',
  siteUrl = 'sternwarte-mannheim.de',
  variant = 'outline',
  size = 'md',
  className,
  onGenerateStart,
  onGenerateComplete,
  onGenerateError,
}: PDFDownloadButtonProps) {
  const [isGenerating, setIsGenerating] = useState(false);

  const handleDownload = useCallback(async () => {
    if (isGenerating) return;

    setIsGenerating(true);
    onGenerateStart?.();

    try {
      // Create PDF document
      const pdfDoc = pdf(
        <ArticlePDF
          article={article}
          siteName={siteName}
          siteUrl={siteUrl}
        />
      );

      // Generate blob
      const blob = await pdfDoc.toBlob();

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const downloadFilename = filename || generatePDFFilename(article.title, 'artikel');
      
      link.href = url;
      link.download = downloadFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Cleanup
      URL.revokeObjectURL(url);

      // Track successful download
      trackDownload(downloadFilename, 'pdf');

      onGenerateComplete?.(downloadFilename);
    } catch (error) {
      const err = error instanceof Error ? error : new Error('PDF generation failed');
      console.error('PDF generation error:', err);
      onGenerateError?.(err);
    } finally {
      setIsGenerating(false);
    }
  }, [
    article,
    filename,
    isGenerating,
    onGenerateComplete,
    onGenerateError,
    onGenerateStart,
    siteName,
    siteUrl,
  ]);

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleDownload}
      isLoading={isGenerating}
      disabled={isGenerating}
      className={className}
      aria-label={isGenerating ? 'PDF wird generiert...' : buttonText}
    >
      {isGenerating ? (
        <>
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          PDF wird erstellt...
        </>
      ) : (
        <>
          <FileDown className="w-4 h-4 mr-2" />
          {buttonText}
        </>
      )}
    </Button>
  );
}

export default PDFDownloadButton;
